import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { 
  Home,
  User,
  CheckCircle2,
  AlertTriangle,
  Phone,
  Mail,
  Lock,
  Upload,
  Camera
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function PublicRegisterInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [inviteValid, setInviteValid] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [inviteData, setInviteData] = useState(null);
  const [registered, setRegistered] = useState(false);

  const [formData, setFormData] = useState({
    username: "",
    password: "",
    confirm_password: "",
    telephone: "",
    email: ""
  });
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  useEffect(() => {
    validateInvite();
  }, [token]);

  const validateInvite = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/public/register-invite/${token}`);
      setInviteValid(true);
      setInviteData(response.data);
      if (response.data.email) {
        setFormData(prev => ({ ...prev, email: response.data.email }));
      }
    } catch (error) {
      setInviteValid(false);
      setInviteError(error.response?.data?.detail || "This invitation is invalid or has expired.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Please select an image file");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image must be less than 5MB");
        return;
      }
      setProfilePhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.username || !formData.password || !formData.telephone || !profilePhoto) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (formData.password !== formData.confirm_password) {
      toast.error("Passwords do not match");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setSubmitting(true);
    try {
      const submitData = new FormData();
      submitData.append("username", formData.username);
      submitData.append("password", formData.password);
      submitData.append("telephone", formData.telephone);
      submitData.append("email", formData.email || "");
      submitData.append("profile_photo", profilePhoto);

      await axios.post(`${API}/public/register-invite/${token}`, submitData);
      setRegistered(true);
      toast.success("Registration successful!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-100 to-stone-200 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-text-muted">Validating invitation...</p>
        </div>
      </div>
    );
  }

  if (!inviteValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-100 to-stone-200 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-red-200">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-red-700 mb-2">Invalid Invitation</h2>
            <p className="text-text-muted mb-6">{inviteError}</p>
            <p className="text-sm text-stone-500">
              Please contact the administrator at WilyRash Homes for a new invitation.
            </p>
            <Button 
              variant="outline" 
              className="mt-6"
              onClick={() => window.location.href = "mailto:wilyrash@nilatglobal.com?subject=Registration Invitation Request"}
            >
              <Mail className="w-4 h-4 mr-2" />
              Contact Admin
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (registered) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-100 to-stone-200 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-green-200">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-green-700 mb-2">Registration Complete!</h2>
            <p className="text-text-muted mb-6">
              Your account has been created successfully. You can now log in to access the WilyRash Homes portal.
            </p>
            <Button 
              onClick={() => navigate("/login")}
              className="bg-primary hover:bg-primary-800"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-100 to-stone-200 py-8 px-4" data-testid="public-register-invite">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
              <Home className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-primary">WilyRash Homes</h1>
          </div>
          <h2 className="text-xl font-semibold text-stone-700">Complete Your Registration</h2>
          {inviteData?.applicant_name && (
            <p className="text-text-muted mt-2">Welcome, {inviteData.applicant_name}!</p>
          )}
        </div>

        {/* Success Banner */}
        <Card className="border-green-200 bg-green-50 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0" />
              <div>
                <p className="font-medium text-green-700">Application Approved!</p>
                <p className="text-sm text-green-600">Create your account to access the tenant portal.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Registration Form */}
        <Card className="border-stone-200 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg">Create Your Account</CardTitle>
            <CardDescription>
              Fill in your details to complete registration
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Profile Photo */}
              <div className="space-y-2">
                <Label>Profile Photo *</Label>
                <div className="flex items-center gap-4">
                  <div className="relative">
                    {photoPreview ? (
                      <img 
                        src={photoPreview} 
                        alt="Preview" 
                        className="w-20 h-20 rounded-full object-cover border-2 border-primary"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-stone-200 flex items-center justify-center">
                        <Camera className="w-8 h-8 text-stone-400" />
                      </div>
                    )}
                    <label 
                      htmlFor="photo-upload"
                      className="absolute bottom-0 right-0 w-8 h-8 bg-primary rounded-full flex items-center justify-center cursor-pointer hover:bg-primary-800 transition-colors"
                    >
                      <Upload className="w-4 h-4 text-white" />
                    </label>
                    <input
                      id="photo-upload"
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      className="hidden"
                      data-testid="photo-upload"
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-text-muted">Upload a clear photo of yourself</p>
                    <p className="text-xs text-stone-400">JPG, PNG (max 5MB)</p>
                  </div>
                </div>
              </div>

              {/* Username */}
              <div className="space-y-2">
                <Label>Username *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                  <Input
                    data-testid="username-input"
                    placeholder="Choose a username"
                    value={formData.username}
                    onChange={(e) => handleChange("username", e.target.value)}
                    className="pl-10 h-12"
                  />
                </div>
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label>Phone Number *</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                  <Input
                    data-testid="telephone-input"
                    placeholder="Enter phone number"
                    value={formData.telephone}
                    onChange={(e) => handleChange("telephone", e.target.value)}
                    className="pl-10 h-12"
                  />
                </div>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label>Email (optional)</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                  <Input
                    type="email"
                    data-testid="email-input"
                    placeholder="Enter email address"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    className="pl-10 h-12"
                  />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label>Password *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                  <Input
                    type="password"
                    data-testid="password-input"
                    placeholder="Create a password"
                    value={formData.password}
                    onChange={(e) => handleChange("password", e.target.value)}
                    className="pl-10 h-12"
                  />
                </div>
              </div>

              {/* Confirm Password */}
              <div className="space-y-2">
                <Label>Confirm Password *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                  <Input
                    type="password"
                    data-testid="confirm-password-input"
                    placeholder="Confirm your password"
                    value={formData.confirm_password}
                    onChange={(e) => handleChange("confirm_password", e.target.value)}
                    className="pl-10 h-12"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-12 bg-primary hover:bg-primary-800 mt-6"
                data-testid="register-btn"
              >
                {submitting ? "Creating Account..." : "Create Account"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-sm text-stone-500 mt-6">
          WilyRash Homes Tenant Management System
        </p>
      </div>
    </div>
  );
}
